/**
 * Admin Updates — table + expandable forms (FAQ / articles pattern).
 */

import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { pickLang } from '../../../utils/i18nDisplay';
import {
  saveUpdate,
  selectUpdateError,
  selectUpdateSaving,
} from '../../../features/updates/updatesSlice';
import {
  BackScreenDiv,
  Button,
  ContentDiv,
  Div,
  H1,
  HeaderDiv,
  ScreenDiv,
  TableColumnDiv,
  TableRowDivLookups,
  UserTableBodyDiv,
  UserTableDiv,
  UserTableRowDiv,
} from '../../../styles/styles';
import AdminUpdateFormPanel, {
  UPDATE_CREATE_FORM_ID,
  updateEditFormId,
} from './adminUpdateFormPanel';

const LANGUAGES = ['en', 'ru', 'sr'];

const emptyTriple = () => ({ en: '', ru: '', sr: '' });

const emptyFormData = () => ({
  publishedAt: dayjs().format('YYYY-MM-DD'),
  title: emptyTriple(),
  body: emptyTriple(),
  webOnly: false,
  mobileOnly: false,
});

const sanitizeTriple = (t) => {
  const obj = {};
  LANGUAGES.forEach((lng) => {
    const v = (t?.[lng] || '').trim();
    if (v) obj[lng] = v;
  });
  return obj;
};

const formDataFromRow = (update) => ({
  publishedAt: update.publishedAt
    ? dayjs(update.publishedAt).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD'),
  title: { ...emptyTriple(), ...(update.title || {}) },
  body: { ...emptyTriple(), ...(update.body || {}) },
  webOnly: Boolean(update.webOnly),
  mobileOnly: Boolean(update.mobileOnly),
});

const titlePreview = (row) =>
  pickLang(row.title || {}, 'en')
  || pickLang(row.title || {}, 'ru')
  || pickLang(row.title || {}, 'sr')
  || row.id;

const fmtPublished = (ms) => {
  if (!ms) return '—';
  try {
    return dayjs(ms).format('YYYY-MM-DD');
  } catch {
    return '—';
  }
};

export default function AdminUpdates({ updates, loading, error, onRefresh }) {
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery({ minWidth: '800px' });
  const saving = useSelector(selectUpdateSaving);
  const saveError = useSelector(selectUpdateError);

  const [createOpen, setCreateOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState(emptyFormData);

  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState(emptyFormData);
  const [localError, setLocalError] = useState(null);

  const openCreate = () => {
    setEditingId(null);
    setEditFormData(emptyFormData());
    setCreateFormData(emptyFormData());
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateFormData(emptyFormData());
  };

  const persistUpdate = async (formData, id = null) => {
    const titles = sanitizeTriple(formData.title);
    const bodies = sanitizeTriple(formData.body);
    if (!Object.keys(titles).length) {
      setLocalError('Title required on at least one language.');
      return false;
    }
    if (!Object.keys(bodies).length) {
      setLocalError('Text required on at least one language.');
      return false;
    }
    setLocalError(null);

    const data = {
      title: titles,
      body: bodies,
      publishedAt: dayjs(formData.publishedAt).toDate(),
      webOnly: formData.webOnly,
      mobileOnly: formData.mobileOnly,
    };
    const result = await dispatch(saveUpdate({ id, data }));
    if (result.meta.requestStatus === 'fulfilled') {
      onRefresh();
      return true;
    }
    return false;
  };

  const handleCreateSubmit = async () => {
    const ok = await persistUpdate(createFormData);
    if (ok) closeCreate();
  };

  const handleEditToggle = (row) => {
    if (editingId === row.id) {
      setEditingId(null);
      setEditFormData(emptyFormData());
      return;
    }
    setCreateOpen(false);
    setEditingId(row.id);
    setEditFormData(formDataFromRow(row));
  };

  const handleEditSubmit = async () => {
    if (!editingId) return;
    const ok = await persistUpdate(editFormData, editingId);
    if (ok) {
      setEditingId(null);
      setEditFormData(emptyFormData());
    }
  };

  const displayError = error || saveError || localError;

  return (
    <>
      <BackScreenDiv grayImage />
      <ScreenDiv column mobileJustifySpace>
        <HeaderDiv />
        <ContentDiv
          gap={!isDesktop && '5px'}
          paddingBack={isDesktop}
          borderRadius="30px"
          alignStart
          justifyStart
          height="auto"
          heightDesktop="auto"
        >
          <Div row width="100%" justify="space-between" align="center">
            {isDesktop && <H1>Updates</H1>}
            <Button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              width="auto"
              greenButton
            >
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </Div>

          {displayError ? (
            <div role="alert" style={{ color: '#c62828', marginBottom: 8 }}>
              {displayError}
            </div>
          ) : null}

          <Div row gap="10px" justifyStart width="100%" height="auto">
            <Button
              type="button"
              greenButton
              width="auto"
              disabled={createOpen}
              onClick={openCreate}
            >
              Create
            </Button>
            {createOpen ? (
              <>
                <Button
                  type="submit"
                  form={UPDATE_CREATE_FORM_ID}
                  greenButton
                  width="auto"
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Create'}
                </Button>
                <Button
                  type="button"
                  outlineButton
                  width="auto"
                  onClick={closeCreate}
                >
                  Close
                </Button>
              </>
            ) : null}
          </Div>

          {createOpen ? (
            <AdminUpdateFormPanel
              title="Create — Update"
              formId={UPDATE_CREATE_FORM_ID}
              formData={createFormData}
              setFormData={setCreateFormData}
              isEdit={false}
              onCancel={closeCreate}
              onSubmit={handleCreateSubmit}
              saving={saving}
            />
          ) : null}

          <Div width="100%">
            <UserTableDiv>
              <TableRowDivLookups>
                <TableColumnDiv>Published</TableColumnDiv>
                <TableColumnDiv>Title</TableColumnDiv>
                <TableColumnDiv>Edit</TableColumnDiv>
              </TableRowDivLookups>

              <UserTableBodyDiv>
                {updates.map((row) => {
                  const preview = titlePreview(row);
                  const isEditing = editingId === row.id;

                  return (
                    <UserTableRowDiv key={row.id}>
                      <TableRowDivLookups>
                        <TableColumnDiv>{fmtPublished(row.publishedAt)}</TableColumnDiv>
                        <TableColumnDiv>{preview}</TableColumnDiv>
                        <TableColumnDiv>
                          <Div row gap="6px" justifyStart wrap="wrap">
                            {isEditing ? (
                              <Button
                                type="submit"
                                form={updateEditFormId(row.id)}
                                greenButton
                                width="auto"
                                fontSize="12px"
                                height="auto"
                                padding="5px 10px"
                                disabled={saving}
                              >
                                {saving ? 'Saving…' : 'Update'}
                              </Button>
                            ) : null}
                            <Button
                              type="button"
                              onClick={() => handleEditToggle(row)}
                              width="auto"
                              outlineButtonGreen
                              fontSize="12px"
                              height="auto"
                              padding="5px 10px"
                            >
                              {isEditing ? 'Close' : 'Edit'}
                            </Button>
                          </Div>
                        </TableColumnDiv>
                      </TableRowDivLookups>

                      {isEditing ? (
                        <AdminUpdateFormPanel
                          title={`Edit — ${preview}`}
                          formId={updateEditFormId(row.id)}
                          formData={editFormData}
                          setFormData={setEditFormData}
                          isEdit
                          onCancel={() => handleEditToggle(row)}
                          onSubmit={handleEditSubmit}
                          saving={saving}
                        />
                      ) : null}
                    </UserTableRowDiv>
                  );
                })}
                {updates.length === 0 && !loading ? (
                  <TableColumnDiv style={{ padding: 12 }}>
                    No updates yet — click Create.
                  </TableColumnDiv>
                ) : null}
              </UserTableBodyDiv>
            </UserTableDiv>
          </Div>
        </ContentDiv>
      </ScreenDiv>
    </>
  );
}
